import * as React from 'react'

import { FileDown } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { IGenerateReportCommandInput, IReportFileInput } from '~/client/graph'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { DOWNLOAD_REPORT_FILE } from '~/client/src/shared/stores/EventStore/eventConstants'
import { toast } from '~/client/src/shared/utils/toast'

// localization: no display text to translate

export interface IReportInfo {
  templateId: string
  projectId: string
  dateFrom: number
  dateTo: number
  isFullTime: boolean
  timezoneId: string
  title: string
  data: any
  files: IReportFileInput[]
}

interface IProps {
  title: string
  getReportInfo: any
  className: string
  onClick?: () => void
  shouldPreventCreation?: boolean
  eventsStore?: DesktopEventStore
  shouldHideIcon?: boolean
  shouldInvertColors?: boolean
  isEnabled?: boolean
}

@inject('eventsStore')
@observer
export default class BaseReportButton extends React.Component<IProps> {
  @observable private isDownloading: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { className, title, isEnabled, shouldPreventCreation } = this.props

    return (
      <Button
        variant="primary-outline"
        className={className}
        disabled={!(isEnabled || shouldPreventCreation)}
        onClick={this.downloadReport}
      >
        {this.icon}
        {title}
      </Button>
    )
  }

  @action.bound
  public downloadReport() {
    const { onClick, getReportInfo, shouldPreventCreation } = this.props
    if (this.isDownloading) {
      return
    }

    if (onClick && shouldPreventCreation) {
      onClick()
    }

    if (!shouldPreventCreation) {
      this.isDownloading = true

      const {
        templateId,
        data,
        projectId,
        dateFrom,
        dateTo,
        timezoneId,
        title,
        files,
        isFullTime,
      } = getReportInfo()

      const generateReportInput: IGenerateReportCommandInput = {
        templateId,
        files,
        title,
        dataJSON: JSON.stringify(data),
        projectId: projectId,
        dateTo,
        dateFrom,
        timezoneId,
        sendEmail: true,
        isFullTime,
        additionalRecipientIds: [],
      }

      this.props.eventsStore.dispatch(
        DOWNLOAD_REPORT_FILE,
        generateReportInput,
        this.onDownloaded,
        this.onDownloadFailed,
      )
    }
  }

  @action.bound
  private onDownloaded() {
    this.isDownloading = false
    this.props.onClick?.()
    toast.success(Localization.translator.aReportWillBeSentToYourEmailShortly)
  }

  @action.bound
  private onDownloadFailed() {
    this.isDownloading = false
    toast.error(Localization.translator.filedToGenerateReport)
  }

  private get icon(): JSX.Element {
    if (this.isDownloading) {
      return <Spinner size="md" className="loader pr25" />
    }
    if (this.props.shouldHideIcon) {
      return null
    }
    return <FileDown data-icon="inline-start" />
  }
}
